<template>
  <div>
    <div class="ski-settings-container">
      <!-- Back Link -->
      <div class="back-link-section">
        <router-link :to="`/${username}`" class="back-link">
          <span class="arrow">&#8592;</span>
          <span class="back-link-text">Back to Profile</span>
        </router-link>
      </div>

      <!-- Header -->
      <div class="header">
        <h2>Ski Settings</h2>
        <p>Configure your waterski setup preferences. When you post a video, we store the settings to the post so you can easily find your historic settings per video.</p>
      </div>

      <!-- Loading State -->
      <div v-if="loading && !skiSettings.boot_placement" class="loading-state">
        <p>Loading ski settings...</p>
      </div>

      <!-- Error State -->
      <div v-if="error" class="error-message">
        {{ error }}
      </div>

      <!-- Ski Settings Form -->
      <form @submit.prevent="updateSkiSettings" class="ski-settings-form">
        <div class="form-group">
          <label for="boot_placement">Boot Placement</label>
          <textarea 
            v-model="skiSettings.boot_placement" 
            id="boot_placement" 
            class="input-field textarea-field" 
            placeholder="Measure from the heel of the front boot to the tail of the ski. e.g. 29 15/16."
          ></textarea>
        </div>

        <div class="form-group">
          <label for="fin_length">Fin Length</label>
          <input 
            type="text" 
            v-model="skiSettings.fin_length" 
            id="fin_length" 
            class="input-field" 
            placeholder="Use the tips of the calipers to measure the length of the fin against the ski. e.g. 6.818" 
          />
        </div>

        <div class="form-group">
          <label for="fin_depth">Fin Depth</label>
          <input 
            type="text" 
            v-model="skiSettings.fin_depth" 
            id="fin_depth" 
            class="input-field" 
            placeholder="Butt the calipers to the base of the ski and measure depth of the fin to the deepest part. e.g. 2.508" 
          />
        </div>

        <div class="form-group">
          <label for="fin_dft">Fin DFT (Distance From Tail)</label>
          <input 
            type="text" 
            v-model="skiSettings.fin_dft" 
            id="fin_dft" 
            class="input-field" 
            placeholder="Butt the calipers to the back of the fin and measure to the tail of the ski. 0.802" 
          />
        </div>

        <div class="form-group">
          <label for="wing_degree">Wing Angle</label>
          <input 
            type="text" 
            v-model="skiSettings.wing_degree" 
            id="wing_degree" 
            class="input-field" 
            placeholder="Place the Wing Angle Gauge between the base of the ski and the wing and then tighten the wing screws. e.g. 8" 
          />
        </div>

        <button type="submit" class="submit-button" :disabled="loading">
          {{ loading ? 'Updating...' : 'Update Ski Settings' }}
        </button>
      </form>
    </div>
  </div>
</template>

<script>
import { API_BASE_URL } from '@/config';
import axios from 'axios';

export default {
  name: 'SkiSettings',
  metaInfo: {
    title: 'Ski Settings - 6buoys',
    meta: [
      { name: 'viewport', content: 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no' }
    ]
  },
  data() {
    return {
      skiSettings: {
        boot_placement: '',
        fin_length: '',
        fin_depth: '',
        fin_dft: '',
        wing_degree: '',
      },
      username: '',
      loading: false,
      error: null,
    };
  },
  async created() {
    await this.fetchUserProfile();
    await this.fetchSkiSettings();
  },
  methods: {
    async fetchUserProfile() {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/profile/`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          },
        });
        this.username = response.data.username;
      } catch (error) {
        console.error('Error fetching user profile:', error);
        this.username = 'edit-profile';
      }
    },
    async fetchSkiSettings() {
      try {
        this.loading = true;
        this.error = null;
        
        const response = await axios.get(`${API_BASE_URL}/api/ski-settings/`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          },
        });

        // Set the ski settings data
        this.skiSettings = {
          boot_placement: response.data.boot_placement || '',
          fin_length: response.data.fin_length || '',
          fin_depth: response.data.fin_depth || '',
          fin_dft: response.data.fin_dft || '',
          wing_degree: response.data.wing_degree || '',
        };
      } catch (error) {
        console.error('Error fetching ski settings:', error);
        this.error = 'Failed to load ski settings. Please try again.';
      } finally {
        this.loading = false;
      }
    },
    async updateSkiSettings() {
      try {
        this.loading = true;
        this.error = null;
        
        // Blur the active element to prevent keyboard issues on mobile
        document.activeElement.blur();
        
        await axios.put(`${API_BASE_URL}/api/ski-settings/`, this.skiSettings, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`,
            'Content-Type': 'application/json',
          },
        });
        
        // Success feedback
        alert('Ski settings updated successfully!');
        
        // Navigate back to user's profile instead of edit profile
        this.$router.push(`/${this.username}`);
        
      } catch (error) {
        console.error('Error updating ski settings:', error);
        this.error = 'Failed to update ski settings. Please try again.';
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
/* Ski Settings Page Styles */

html, body {
  overscroll-behavior: contain; /* Prevent scrolling when input is focused */
}

.back-link-section {
  text-align: center;
  margin-top: 20px;
  margin-bottom: 20px;
}

.back-link {
  color: #ffffff;
  text-decoration: none;
  font-weight: bold;
}

.back-link:hover {
  color: #1da1f2;
}

.arrow {
  font-size: 1.5em;
  margin-right: 8px;
  vertical-align: middle;
}

.ski-settings-container {
  max-width: 500px;
  margin: 20px auto;
  padding: 20px;
  background-color: #192734;
  border-radius: 16px;
  color: #d9d9d9;
  padding-bottom: 80px;
  margin-bottom: 200px;
}

.header {
  text-align: center;
  margin-bottom: 30px;
}

.header h2 {
  margin: 0;
  color: #1da1f2;
  font-size: 1.8em;
}

.header p {
  margin: 8px 0 0 0;
  color: #8899a6;
  font-size: 0.9em;
}

.loading-state {
  text-align: center;
  padding: 40px 20px;
  color: #8899a6;
}

.error-message {
  color: #e74c3c;
  text-align: center;
  margin: 15px 0;
  padding: 10px;
  background-color: rgba(231, 76, 60, 0.1);
  border-radius: 8px;
  font-size: 0.9em;
}

.ski-settings-form .form-group {
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.form-group label {
  margin-bottom: 8px;
  font-weight: bold;
  color: #d9d9d9;
  font-size: 0.95em;
}

.input-field {
  width: 96%;
  padding: 12px;
  background-color: #15202b;
  border: 1px solid #38444d;
  border-radius: 8px;
  color: #d9d9d9;
  font-size: 16px;
  transition: border-color 0.3s ease;
}

.input-field:focus {
  outline: none;
  border-color: #1da1f2;
}

.textarea-field {
  resize: vertical;
  min-height: 80px;
  font-size: 16px;
  font-family: inherit;
}

.input-field::placeholder {
  color: #8899a6;
}

.submit-button {
  width: 100%;
  padding: 14px;
  background-color: #1da1f2;
  border: none;
  border-radius: 10px;
  color: #fff;
  font-weight: bold;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.submit-button:hover:not(:disabled) {
  background-color: #0c8ddb;
}

.submit-button:disabled {
  background-color: #38444d;
  cursor: not-allowed;
  opacity: 0.7;
}

/* Mobile responsive */
@media screen and (max-width: 600px) {
  .ski-settings-container {
    margin: 10px;
    margin-bottom: 80px;
    padding: 15px;
  }
  
  .input-field {
    width: 94%;
  }
}
</style> 